import moment from "moment";
import { SocialPost } from "types/campaign/collaborations";
import { SocialAccount } from "types/influencer";

export interface SocialAccountData {
    status: string;
    social_account: SocialAccount,
    permissions: Record<string, string>,
    track_media?: {
        status: string,
        last_fetched: string,
        refresh_allowed: boolean
    }
}

export interface SocialAccountState {
    loading: boolean,
    error: string | null,
    list: {
        items: SocialAccountData[],
        last_fetched_at: number,
        error: string | null
        loading: boolean,
    },
    items: Record<string, {
        account: SocialAccount,
        last_fetched_at: number
    }>
    trackedPosts: {
        applied_filter_id: string | null,
        loading: boolean,
        error: string | null,
        filters: TrackedPostsFilters,
        items: Record<string, { // SocialAccountId
            total: number,
            filtered_lists: Record<string, //FilterId
                {
                    items: TrackedPost[],
                    last_fetched_at: number,
                    error: string | null,
                    loading: boolean,
                    count: number,
                    has_more: boolean,
                    page: number,
                    filters: TrackedPostsFilters,
                    expired: boolean
                }>
        }>
    },
    creators: {
        applied_filter_id: string | null,
        loading: boolean,
        error: string | null,
        filters: SocialAccountCreatorsFilters,
        items: Record<string, { // SocialAccountId
            total: number,
            filtered_lists: Record<string, //FilterId
                {
                    items: SocialAccountCreator[],
                    last_fetched_at: number,
                    error: string | null,
                    loading: boolean,
                    count: number,
                    has_more: boolean,
                    page: number,
                    filters: SocialAccountCreatorsFilters,
                    expired: boolean
                }>
        }>
    },
    content: {
        applied_filter_id: string | null,
        loading: boolean,
        error: string | null,
        filters: SocialAccountContentFilters,
        items: Record<string, { // SocialAccountId
            total: number,
            filtered_lists: Record<string, //FilterId
                {
                    items: SocialAccountContentItem[],
                    last_fetched_at: number,
                    error: string | null,
                    loading: boolean,
                    count: number,
                    has_more: boolean,
                    page: number,
                    filters: SocialAccountContentFilters,
                    expired: boolean
                }>
        }>
    }
}

export interface CommentUser {
    id: string;
    username: string;
}

export interface Comment {
    id: string;
    text: string;
    like_count: number;
    hidden: boolean;
    timestamp: string;
    from: CommentUser;
    replies?: {
        data: Comment[];
    };
}

export interface TrackedPost {
    is_tagged: boolean,
    is_mentioned: boolean,
    social_post: SocialPost
}


export interface TrackedPostsFilters {
    page: number,
    [key: string]: any
}

export interface SocialAccountCreatorsFilters {
    page: number,
    [key: string]: any
}

export interface SocialAccountCreator {
    social_account: SocialAccount,
    engagement_count: number,
    like_count: number,
    comments_count: number,
    views_count: number,
    no_of_posts: number
}

export enum SortOptions {
    POSTED_DATE = 'timestamp_tz',
    LIKE_COUNT = 'like_count',
    VIEWS_COUNT = 'views_count',
    ENGAGEMENT_SCORE = 'engagement_score',
    // AVERAGE_VIEWS = 'average_views',
}

export const SortOptionLabel = {
    [SortOptions.POSTED_DATE]: 'Posted Date',
    [SortOptions.LIKE_COUNT]: 'Like Count',
    [SortOptions.VIEWS_COUNT]: 'Views Count',
    [SortOptions.ENGAGEMENT_SCORE]: 'Engagement Score',
}

export const defaultTrackedPostsFilters = {
    page: 1,
    sort: SortOptions.POSTED_DATE,
    end_date: moment().format('YYYY-MM-DD'),
    start_date: moment().subtract(3, 'months').format('YYYY-MM-DD')
}

export enum PostTypeOptions {
    ALL = 'all',
    FEED = 'FEED',
    REELS = 'REELS',
}

export const PostTypeOptionLabel = {
    [PostTypeOptions.ALL]: 'All',
    [PostTypeOptions.FEED]: 'Feed',
    [PostTypeOptions.REELS]: 'Reels',
}

export enum ReferenceType {
    ALL = 'all',
    TAGGED = 'is_tagged',
    MENTIONED = 'is_mentioned',
}

export const ReferenceTypeLabel = {
    [ReferenceType.ALL]: 'All',
    [ReferenceType.MENTIONED]: 'Mentioned',
    [ReferenceType.TAGGED]: 'Tagged'
}

export enum CreatorsSortOptions {
    POSTED_DATE='last_post_timestamp',
    FOLLOWERS = 'followers_count',
    LIKE_COUNT = 'like_count',
    VIEWS_COUNT = 'views_count',
    POSTS = 'no_of_posts',
    COMMENTS_COUNT = 'comments_count'
}

export const CreatorsSortOptionLabel = {
    [CreatorsSortOptions.POSTED_DATE]: 'Posted Date',
    [CreatorsSortOptions.FOLLOWERS]: 'Followers Count',
    [CreatorsSortOptions.LIKE_COUNT]: 'Likes Count',
    [CreatorsSortOptions.VIEWS_COUNT]: 'Views Count',
    [CreatorsSortOptions.COMMENTS_COUNT]: 'Comments Count',
    [CreatorsSortOptions.POSTS]: 'Posts Count',
}

export const defaultSocialAccountCreatorsFilters = {
    page: 1,
    sort: CreatorsSortOptions.POSTED_DATE,
    end_date: moment().format('YYYY-MM-DD'),
    start_date: moment().subtract(3, 'months').format('YYYY-MM-DD')
}

export interface SocialAccountContentFilters {
    page: number,
    [key: string]: any
}

export interface SocialAccountContentItem {
    social_post: SocialPost
}

export const defaultSocialAccountContentFilters = {
    page: 1,
    sort: SortOptions.POSTED_DATE,
    end_date: moment().format('YYYY-MM-DD'),
    start_date: moment().subtract(3, 'months').format('YYYY-MM-DD')
}